/**
 * Smoothstep edge whose middle segment can be dragged: the horizontal segment
 * of a downward edge slides up/down, the vertical segment of an edge looping
 * back upward slides left/right. The corners are still computed by
 * getSmoothStepPath, so they follow the nodes when they move. Double-click the
 * segment to reset it.
 */

import React, { createContext, memo, useCallback, useContext } from 'react';
import { BaseEdge, EdgeProps, Position, getSmoothStepPath, useReactFlow } from 'reactflow';
import '../nodes/TaskNode.css';
import './MovableEdge.css';

// getSmoothStepPath's default distance between a handle and the first corner.
const HANDLE_OFFSET = 20;

export interface EdgeRoute {
  // Downward edges: where the horizontal segment sits between the two nodes,
  // 0 = right below the source, 1 = right above the target. Stored as a
  // fraction so it keeps its relative place when the nodes move.
  t?: number;
  // Upward (looping) edges: horizontal offset of the vertical segment from
  // the default midpoint between source and target.
  dx?: number;
}

export interface MovableEdgeData {
  route?: EdgeRoute;
  [key: string]: unknown;
}

// Provided by PipelineCanvas; null (readonly canvas) disables dragging.
export const EdgeRouteContext = createContext<
  ((edgeId: string, route: EdgeRoute | null) => void) | null
>(null);

export const MovableEdge = memo<EdgeProps<MovableEdgeData>>(({
  id,
  sourceX,
  sourceY,
  targetX,
  targetY,
  sourcePosition,
  targetPosition,
  data,
  label,
  labelStyle,
  labelShowBg,
  labelBgStyle,
  labelBgPadding,
  labelBgBorderRadius,
  markerEnd,
  style,
}) => {
  const { screenToFlowPosition } = useReactFlow();
  const setRoute = useContext(EdgeRouteContext);
  const route = data?.route;

  // All node handles are Bottom (source) -> Top (target); any other combination
  // just renders a plain smoothstep edge.
  const movable = sourcePosition === Position.Bottom && targetPosition === Position.Top;
  const sourceGapY = sourceY + HANDLE_OFFSET;
  const targetGapY = targetY - HANDLE_OFFSET;
  const downward = sourceGapY <= targetGapY;

  let centerX: number | undefined;
  let centerY: number | undefined;
  if (movable && downward && route?.t !== undefined) {
    centerY = sourceGapY + route.t * (targetGapY - sourceGapY);
  } else if (movable && !downward && route?.dx !== undefined) {
    centerX = (sourceX + targetX) / 2 + route.dx;
  }

  const [path, labelX, labelY] = getSmoothStepPath({
    sourceX, sourceY, sourcePosition, targetX, targetY, targetPosition, centerX, centerY,
  });

  // The draggable middle segment, matching the path getSmoothStepPath drew.
  const segX = centerX ?? (sourceX + targetX) / 2;
  const segY = centerY ?? (sourceY + targetY) / 2;
  const segment = downward
    ? { x1: sourceX, y1: segY, x2: targetX, y2: segY }
    : { x1: segX, y1: sourceGapY, x2: segX, y2: targetGapY };
  const segmentLength = Math.abs(segment.x2 - segment.x1) + Math.abs(segment.y2 - segment.y1);

  const handlePointerDown = useCallback(
    (event: React.PointerEvent<SVGLineElement>) => {
      if (!setRoute || event.button !== 0) return;
      event.stopPropagation();

      const handle = event.currentTarget;
      handle.setPointerCapture(event.pointerId);
      const defaultCenterX = (sourceX + targetX) / 2;

      const onMove = (e: PointerEvent) => {
        const point = screenToFlowPosition({ x: e.clientX, y: e.clientY });
        if (downward) {
          const span = targetGapY - sourceGapY;
          const t = span > 0 ? (point.y - sourceGapY) / span : 0.5;
          setRoute(id, { t: Math.min(1, Math.max(0, t)) });
        } else {
          setRoute(id, { dx: point.x - defaultCenterX });
        }
      };
      const onUp = (e: PointerEvent) => {
        handle.releasePointerCapture(e.pointerId);
        handle.removeEventListener('pointermove', onMove);
        handle.removeEventListener('pointerup', onUp);
      };
      handle.addEventListener('pointermove', onMove);
      handle.addEventListener('pointerup', onUp);
    },
    [id, setRoute, screenToFlowPosition, downward, sourceX, targetX, sourceGapY, targetGapY]
  );

  const handleDoubleClick = useCallback(
    (event: React.MouseEvent) => {
      event.stopPropagation();
      setRoute?.(id, null);
    },
    [id, setRoute]
  );

  return (
    <>
      <BaseEdge
        path={path}
        markerEnd={markerEnd}
        style={style}
        label={label}
        labelX={labelX}
        labelY={labelY}
        labelStyle={labelStyle}
        labelShowBg={labelShowBg}
        labelBgStyle={labelBgStyle}
        labelBgPadding={labelBgPadding}
        labelBgBorderRadius={labelBgBorderRadius}
      />
      {setRoute && movable && segmentLength > 4 && (
        <line
          {...segment}
          className={`movable-edge-segment nodrag nopan ${downward ? 'horizontal' : 'vertical'}`}
          onPointerDown={handlePointerDown}
          onDoubleClick={handleDoubleClick}
        >
          <title>Drag to move this segment, double-click to reset</title>
        </line>
      )}
    </>
  );
});

MovableEdge.displayName = 'MovableEdge';
